// Filled fields (02-building-blocks.md, Dialog): the label is the field's accessible name and,
// unless a placeholder is given, sits in the empty field as its placeholder.
import { useId, type InputHTMLAttributes } from 'react';
import { t } from '../i18n';

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** A character count under the field, at the right. */
  count?: string;
}

export function Field({ label, count, className, placeholder, ...input }: FieldProps) {
  const id = useId();
  return (
    <div className={`field${placeholder ? ' field-fixed' : ''}${className ? ` ${className}` : ''}`}>
      <input id={id} placeholder={placeholder ?? ' '} {...input} />
      <label htmlFor={id}>{label}</label>
      {count && <span className="field-count">{count}</span>}
    </div>
  );
}

/**
 * A search or filter field (People's "Find a person", the search sheet, a member or emoji filter):
 * never filled in by the browser or a password manager (02, v1.58.36).
 */
export const noAutofill = { autoComplete: 'off', 'data-1p-ignore': '', 'data-lpignore': 'true', 'data-bwignore': '' } as const;

/** "Your password": the browser's current-password autofill. */
export const PasswordField = (props: Omit<FieldProps, 'label'>) => (
  <Field label={t('field.yourPassword')} type="password" autoComplete="current-password" {...props} />
);

/** A switch row (02-building-blocks.md): the title names the switch, a line of text under it. */
export function Switch({ label, text, checked, disabled, onChange }: { label: string; text?: string; checked: boolean; disabled?: boolean; onChange: (on: boolean) => void }) {
  const id = useId();
  return (
    <div className="switch-row">
      <span>
        <span className="switch-title" id={id}>
          {label}
        </span>
        {text && <span className="switch-text">{text}</span>}
      </span>
      <button type="button" role="switch" className="switch" aria-checked={checked} aria-labelledby={id} disabled={disabled} onClick={() => onChange(!checked)} />
    </div>
  );
}
